import { Head, Link } from "@inertiajs/react"
import type { Video } from "@/types"
import DashboardLayout from "@/components/dashboard/layout"
import VideoCard from "@/components/dashboard/videos/video-card"
import VideoRow from "@/components/dashboard/videos/video-row"
import IndexToolbar from "@/components/dashboard/index-toolbar"
import Pagination from "@/components/pagination"
import { useViewPreference } from "@/hooks/use-view-preference"
import { Button } from "@/components/ui/button"

interface PaginationMeta {
  page: number
  limit: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

const VIDEO_FILTERS = [
  {
    label: "All statuses",
    param: "status",
    options: [
      { value: "draft", label: "In progress" },
      { value: "scripted", label: "Scripted" },
      { value: "rendered", label: "Rendered" },
      { value: "published", label: "Published" },
      { value: "archived", label: "Archived" },
    ],
  },
  {
    label: "All formats",
    param: "aspect_ratio",
    options: [
      { value: "portrait", label: "Portrait (9:16)" },
      { value: "landscape", label: "Landscape (16:9)" },
      { value: "square", label: "Square (1:1)" },
    ],
  },
]

const VIDEO_SORT_OPTIONS = [
  { value: "updated_at", label: "Last updated" },
  { value: "created_at", label: "Date added" },
  { value: "name", label: "Name" },
]

export default function VideosIndex({
  videos,
  pagination,
  current_role,
  filters = {},
}: {
  videos: Video[]
  pagination: PaginationMeta
  current_role: string
  filters: Record<string, string>
}) {
  const [view, setView] = useViewPreference("grid")

  return (
    <DashboardLayout>
      <Head title="Videos | QuiteRight" />

      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-charcoal">
            Videos
          </h1>
          <p className="mt-1 text-warm-grey">
            Your property video ads.
          </p>
        </div>
      </div>

      {pagination.count === 0 && !hasActiveFilters(filters) ? (
        <EmptyState />
      ) : (
        <>
          <IndexToolbar
            filters={VIDEO_FILTERS}
            sortOptions={VIDEO_SORT_OPTIONS}
            currentFilters={filters}
            currentSort={filters.sort || "updated_at"}
            currentDirection={filters.direction || "desc"}
            view={view}
            onViewChange={setView}
            baseUrl="/dashboard/videos"
            count={pagination.count}
            noun="video"
          />

          {videos.length === 0 ? (
            <NoResults />
          ) : view === "grid" ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {videos.map((video) => (
                <VideoCard
                  key={video.id}
                  video={video}
                  currentRole={current_role}
                  showAddress
                />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {videos.map((video) => (
                <VideoRow
                  key={video.id}
                  video={video}
                  currentRole={current_role}
                />
              ))}
            </div>
          )}

          <Pagination
            pagination={pagination}
            baseUrl="/dashboard/videos"
            params={filters}
          />
        </>
      )}
    </DashboardLayout>
  )
}

function hasActiveFilters(filters: Record<string, string>): boolean {
  return Object.entries(filters).some(([key, val]) => val && key !== "sort" && key !== "direction")
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-stone/60 bg-white px-8 py-16 text-center">
      <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-stone/20">
        <svg className="size-8 text-warm-grey" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
        </svg>
      </div>
      <h2 className="mb-2 font-display text-xl font-semibold text-charcoal">
        No videos yet
      </h2>
      <p className="mb-6 max-w-sm text-warm-grey">
        Create your first video ad from any listing. Pick a style, generate a script, and add a voiceover.
      </p>
      <Button asChild className="bg-red text-white hover:bg-deep-red">
        <Link href="/dashboard/listings">Browse listings</Link>
      </Button>
    </div>
  )
}

function NoResults() {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-stone/60 bg-white px-8 py-12 text-center">
      <p className="text-sm text-warm-grey">No videos match your filters.</p>
    </div>
  )
}
